/**
 * Header search: the field slides open on hover and results stay in the header.
 */
class PredictiveSearch {
  constructor() {
    this.root = document.querySelector('[data-header-search]');
    if (!this.root) return;

    this.form = this.root.querySelector('form.searchform');
    this.input = this.root.querySelector('input[type="search"]');
    this.button = this.root.querySelector('.thb-quick-search');
    this.panel = this.root.querySelector('.thb-header-search__panel');
    this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.hoverTimer = null;
    this.controller = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const wait = typeof debounce === 'function'
      ? debounce
      : (fn, delay) => {
          let timer;
          return function debounced(...args) {
            clearTimeout(timer);
            timer = setTimeout(() => fn.apply(this, args), delay);
          };
        };

    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', wait(() => {
      this.onChange();
    }, 180));
    this.input.addEventListener('focus', () => {
      this.open();
      if (this.getQuery()) this.onChange();
    });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true, true);
      }
    });

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      if (!this.root.classList.contains('is-open')) {
        this.open();
        this.input.focus({ preventScroll: true });
        return;
      }
      if (this.getQuery()) {
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
        else this.form.submit();
      } else {
        this.input.focus({ preventScroll: true });
      }
    });

    if (this.canHover) {
      this.root.addEventListener('mouseenter', () => {
        this.open();
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => {
          if (this.root.matches(':hover')) this.input.focus({ preventScroll: true });
        }, 120);
      });
      this.root.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimer);
        if (!this.getQuery()) this.close(true);
      });
    }

    document.addEventListener('click', (event) => {
      if (!this.root.contains(event.target)) this.close(true);
    });

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close(true, true);
    });
  }

  open() {
    const header = document.getElementById('header');
    if (header) {
      document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    }
    this.root.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.hidePanel();
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  getSearchResults(searchTerm) {
    if (this.controller) this.controller.abort();
    this.controller = typeof AbortController === 'function' ? new AbortController() : null;

    this.showPanel();
    this.panel.classList.add('is-loading');
    this.panel.innerHTML = '';

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,article,query',
      'resources[limit]': '8',
      section_id: 'predictive-search'
    });
    const url = `${theme.routes.predictive_search_url}?${params.toString()}`;

    fetch(url, this.controller ? { signal: this.controller.signal } : undefined)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        if (this.getQuery() !== searchTerm) return;
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        this.renderSearchResults(section ? section.innerHTML : '');
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        this.hidePanel();
      });
  }

  renderSearchResults(resultsMarkup) {
    this.panel.classList.remove('is-loading');
    this.panel.innerHTML = resultsMarkup || '';
    if (!this.panel.textContent.trim()) {
      this.hidePanel();
      return;
    }

    this.panel.querySelectorAll('img').forEach((img) => {
      const srcset = img.getAttribute('data-srcset') || '';
      const url = srcset.trim().split(/\s+/)[0];
      if (url) img.src = url;
    });
    this.panel.querySelectorAll('[tabindex="-1"]').forEach((el) => el.removeAttribute('tabindex'));

    this.panel.querySelectorAll('.side-panel-content--tabs button').forEach((button, index) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const tabs = button.parentElement.querySelectorAll('button');
        const panels = this.panel.querySelectorAll('.side-panel-content--tab-panel');
        tabs.forEach((tab) => tab.classList.toggle('tab-active', tab === button));
        panels.forEach((item, panelIndex) => item.classList.toggle('tab-active', panelIndex === index));
      });
    });

    const submitButton = this.panel.querySelector('#search-results-submit');
    if (submitButton) {
      submitButton.addEventListener('click', (event) => {
        event.preventDefault();
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
        else this.form.submit();
      });
    }
    this.showPanel();
  }

  showPanel() {
    if (this.panel.hidden) {
      this.panel.hidden = false;
      requestAnimationFrame(() => this.panel.classList.add('is-visible'));
    } else {
      this.panel.classList.add('is-visible');
    }
    this.open();
  }

  hidePanel() {
    this.panel.classList.remove('is-visible', 'is-loading');
    this.panel.hidden = true;
    this.panel.innerHTML = '';
  }

  close(blur, force) {
    clearTimeout(this.hoverTimer);
    if (blur && document.activeElement === this.input) this.input.blur();
    this.hidePanel();
    if (force || !this.getQuery()) {
      this.root.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
    }
  }
}

window.addEventListener('load', () => {
  if (typeof PredictiveSearch !== 'undefined') new PredictiveSearch();
});
